<template>
  <s-modal
    :title="t('title.modal', {type: t('blockTypes.' + blockType?.translationKey)})"
    v-model:open="internalOpen"
    :confirm="{
      label: t('actions.save'),
      icon: 'floppy',
    }"
    :cancel="{
      label: t('actions.cancel'),
      icon: 'close',
    }"
    @confirm="save"
    @cancel="close"
    width="xl"
  >
    <template #header-right>
      <div v-if="variables && variables.length" class="flex gap-1">
        <span class="font-bold opacity-60">
          {{ t('labels.variables') }}
        </span>
        <task-variable-chip
          v-for="variable in variables"
          :key="variable.name"
          :variable="variable"
        />
      </div>

      <s-badge v-else class="leading-none text-gray-400 italic">
        <span class="opacity-70">
          {{ t('placeholder.noVariables') }}
        </span>
      </s-badge>
    </template>
    <s-modal-content>
      <div
        v-if="errors?.componentGrader?.uuid || errors?.uiConfig?.uuid"
        class="text-red-600 text-lg font-bold"
      >
        <s-icon name="warning" />
        {{ errors?.componentGrader?.uuid || errors?.uiConfig?.uuid }}
      </div>
      <div class="flex flex-col gap-4 pb-1">
        <div v-if="blockType?.sharedValues?.length">
          <s-input-field
            :id="`name-${uiConfig.uuid}`"
            :label="t('labels.name')"
            :description="t('descriptions.name')"
            v-model="clonedUiConfig.name"
            class="flex-1 w-full"
          />
          <s-label-value :label="t('labels.shared-values')" class="mt-2">
            <div class="flex flex-wrap gap-2 items-center mt-0.5">
              <s-badge v-for="value in blockType.sharedValues" size="sm">
                {{ slugify(clonedUiConfig.name) }}_{{ value }}
              </s-badge>
            </div>
          </s-label-value>
        </div>
        <data-driven-component
          :form
          :task-id="taskId"
          :files="files"
          :component-module="blockFieldsModule"
          :all-data="mergedData"
          :errors="mergedErrors"
          :variables
          @update:all-data="updateMergedData($event)"
        />
      </div>
    </s-modal-content>

    <template v-if="isDirty" #confirmClose="{close, cancel}">
      <s-modal
        :open="true"
        :title="t('unsavedChanges.title')"
        :message="t('unsavedChanges.message')"
        :confirm="{
          label: t('actions.discard'),
          icon: 'trash-can',
          color: 'red',
        }"
        @confirm="
          () => {
            close();
            emit('close');
          }
        "
        @cancel="cancel"
      />
    </template>
  </s-modal>
</template>

<script setup lang="ts">
import SInputField from '../../../design-system/SInputField.vue';
import SBadge from '../../../design-system/SBadge.vue';
import DataDrivenComponent from '../../../components/tasks/DataDrivenComponent.vue';
import {computed, toRef} from 'vue';
import {useCloned, useVModel} from '@vueuse/core';
import isEqual from 'lodash/isEqual';
import SModal from '../../../design-system/SModal.vue';
import SModalContent from '../../../design-system/SModalContent.vue';
import Block = App.Entities.Tasks.Ui.Block;
import TaskComponentGrader = App.Entities.Tasks.ComponentGraders.TaskComponentGrader;
import TaskFileUploadDto = App.DTOs.Tasks.TaskFileUploadDto;
import {useBlockTypeLibrary} from '../../../blocks/useBlockTypeLibrary';
import {AnyTaskVariableDto} from '../../../types/entities/tasks';
import {useI18n} from 'vue-i18n';
import SLabelValue from '../../../design-system/SLabelValue.vue';
import TaskVariableChip from '../../../chips/TaskVariableChip.vue';
import SIcon from '../../../design-system/SIcon.vue';

const props = defineProps<{
  form: any;
  taskId?: number;
  files: TaskFileUploadDto[];
  uiConfig: Block;
  componentGrader: TaskComponentGrader | null;
  errors?: Record<string, any>;
  variables?: AnyTaskVariableDto[];
  open: boolean;
}>();

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const emit = defineEmits(['close', 'update:uiConfig', 'update:componentGrader', 'update:open']);
const internalOpen = useVModel(props, 'open', emit);

const {cloned: clonedUiConfig} = useCloned(toRef(props, 'uiConfig'));
const {cloned: clonedComponentGrader} = useCloned(toRef(props, 'componentGrader'));

const mergedData = computed(() => ({
  ...clonedComponentGrader.value,
  ...clonedUiConfig.value,
}));

const mergedErrors = computed(() => ({
  ...props.errors?.componentGrader,
  ...props.errors?.uiConfig,
}));

const isDirty = computed(() => {
  const isUiDirty = !isEqual(props.uiConfig, clonedUiConfig.value);
  const isGraderDirty = !isEqual(props.componentGrader, clonedComponentGrader.value);
  return isUiDirty || isGraderDirty;
});

function updateMergedData(allData: Record<string, any>) {
  Object.entries(allData).forEach(([key, value]) => {
    if (clonedUiConfig.value.hasOwnProperty(key)) {
      clonedUiConfig.value[key as keyof Block] = value;
    } else if (clonedComponentGrader.value?.hasOwnProperty(key)) {
      clonedComponentGrader.value[key as keyof TaskComponentGrader] = value;
    }
    emit(`update:${key}` as any, [value]);
  });
}

const save = () => {
  emit('update:uiConfig', clonedUiConfig.value);
  emit('update:componentGrader', clonedComponentGrader.value);
  emit('close');
};

const close = () => {
  emit('close');
};

const blockTypeLibrary = useBlockTypeLibrary();
const blockType = computed(() => blockTypeLibrary.find(props.uiConfig.type));

const blockFieldsModule = computed(() => {
  let blockTypeId = props.uiConfig.type;

  if (blockTypeId === 'SingleCalculation') {
    blockTypeId = 'Calculation';
  }

  if (blockTypeId === 'TextBlock') {
    blockTypeId = 'Text';
  }

  return import(`../../../blocks/${blockTypeId}/ConfigFields.vue`);
});

function slugify(name: string) {
  if (!name) return '';

  // make lower case and trim
  var slug = name.toLowerCase().trim();

  // remove accents from charaters
  slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // replace invalid chars with spaces
  slug = slug.replace(/[^a-z0-9\s-]/g, ' ').trim();

  // replace multiple spaces or hyphens with a single hyphen
  slug = slug.replace(/[\s-]+/g, '_');

  return slug;
}
</script>
<i18n>
{
  "en": {
    "actions.discard": "Discard Changes",
    "descriptions.name": "The name of the block. This will be used to generate field names.",
    "labels.name": "Name",
    "labels.shared-values": "Shared Values",
    "labels.variables": "Variables:",
    "placeholder.noVariables": "No variables available",
    "title.modal": "{type} Block",
    "tagline.modal": "Editing",
    "unsavedChanges.title": "Unsaved Changes",
    "unsavedChanges.message": "You have unsaved changes. Are you sure you want to close this form?"
  },
  "fr": {
    "actions.discard": "Abandonner les modifications",
    "descriptions.name": "Le nom du bloc. Il sera utilisé pour générer les noms des champs.",
    "labels.name": "Nom",
    "labels.shared-values": "Valeurs partagées",
    "labels.variables": "Variables:",
    "placeholder.noVariables": "Aucune variable disponible",
    "title.modal": "Bloc {type}",
    "tagline.modal": "Édition",
    "unsavedChanges.title": "Modifications non enregistrées",
    "unsavedChanges.message": "Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir fermer ce formulaire ?"
  }
}
</i18n>
